// Hero background reel: slow, calm crossfades with a subtle camera drift.
const DWELL = 5000;
const BLEND = 1150;
const WORDS = ['aura', 'pulse', 'craft', 'play', 'soul', 'edge'];

export function initReel(root = document.querySelector('[data-hero]')) {
  if (!root) return { start() {}, pause() {} };

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(pointer: fine)').matches;
  const shift = root.querySelector('[data-reel-shift]');
  const items = [...root.querySelectorAll('[data-reel-item]')];
  const bar = root.querySelector('[data-hud-bar]');
  const idxEl = root.querySelector('[data-hud-index]');
  const totEl = root.querySelector('[data-hud-total]');
  const titEl = root.querySelector('[data-hud-title]');
  const toggle = root.querySelector('[data-hud-toggle]');
  const prev = root.querySelector('[data-reel-prev]');
  const next = root.querySelector('[data-reel-next]');
  const nextPreview = root.querySelector('[data-reel-next-preview]');
  const nextPreviewImage = root.querySelector('[data-reel-next-image]');
  const nextPreviewTitle = root.querySelector('[data-reel-next-title]');
  const currentIndex = root.querySelector('[data-reel-current-index]');
  const currentDescription = root.querySelector('[data-reel-current-description]');
  const dotsRoot = root.querySelector('[data-reel-dots]');
  const em = root.querySelector('[data-swap]');
  const n = items.length;
  if (!n) return { start() {}, pause() {} };

  const dots = dotsRoot ? items.map((item, itemIndex) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'heroControls__dot';
    button.textContent = String(itemIndex + 1).padStart(2, '0');
    button.setAttribute('aria-label', `Show slide ${itemIndex + 1}: ${item.dataset.title || ''}`);
    button.addEventListener('click', (event) => { event.stopPropagation(); advance(itemIndex); });
    dotsRoot.appendChild(button);
    return button;
  }) : [];

  // a slide holds either an <img> or a <video> — everything below reads the
  // slide through these three helpers so neither case needs a special path
  const mediaOf = (item) => item.querySelector('img, video');
  // videos only run while their slide is on screen; the poster doubles as the
  // still frame for the UP NEXT thumbnail, since a <video> has no src to crib
  const stillOf = (item) => {
    const el = mediaOf(item);
    if (!el) return '';
    return el.tagName === 'VIDEO' ? (el.poster || '') : (el.currentSrc || el.src);
  };
  const playMedia = (item, restart = true) => {
    const video = item?.querySelector('video');
    if (!video) return;
    if (restart) { try { video.currentTime = 0; } catch { /* not seekable yet */ } }
    video.play?.().catch(() => { /* autoplay blocked — poster stands in */ });
  };
  const stopMedia = (item) => item?.querySelector('video')?.pause?.();

  // UP NEXT wants a frame of the film it is announcing, and a <video> has no
  // thumbnail to hand out. So we paint one: seek the (idle, off-screen) clip a
  // little way in — frame zero is usually a fade-up — and draw it to a canvas.
  // Same-origin, so the canvas stays untainted; one grab per clip, cached.
  const frames = new Map();
  function filmFrame(film, use) {
    const key = film.currentSrc || film.src;
    if (frames.has(key)) { use(frames.get(key)); return; }

    const draw = () => {
      const w = film.videoWidth, h = film.videoHeight;
      if (!w || !h) return;
      try {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 480 / w);            // thumb is 180px wide
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        canvas.getContext('2d').drawImage(film, 0, 0, canvas.width, canvas.height);
        const url = canvas.toDataURL('image/jpeg', .82);
        frames.set(key, url);
        use(url);
      } catch { /* tainted or no context — the poster stands in */ }
    };

    if (film.readyState < 2) {                          // no decoded frame yet
      film.addEventListener('loadeddata', () => filmFrame(film, use), { once: true });
      return;
    }
    const at = Math.min(1.2, (film.duration || 2) * .25);
    if (Math.abs(film.currentTime - at) < .05) { draw(); return; }
    film.addEventListener('seeked', draw, { once: true });
    film.currentTime = at;                              // playMedia rewinds later
  }

  let index = 0;
  let live = false;           // true once the loader is out of the way
  let previewSource = '';     // what the UP NEXT thumbnail is currently showing
  let paused = false;
  let animating = false;
  let barAnim = null;
  let timer = null;
  let wordIndex = 0;
  let wordSpan = null;
  let hasSyncedHeroMeta = false;

  if (totEl) totEl.textContent = String(n).padStart(2, '0');
  syncHud();
  playMedia(items[index]);

  if (em) {
    em.textContent = '';
    wordSpan = makeWord(WORDS[0]);
    em.appendChild(wordSpan);
  }

  function makeWord(word) {
    const span = document.createElement('span');
    span.setAttribute('data-swap-word', '');
    span.textContent = word;
    return span;
  }

  function syncHud() {
    if (idxEl) idxEl.textContent = String(index + 1).padStart(2, '0');
    if (titEl) titEl.textContent = items[index].dataset.title || '';
    transitionHeroMeta(currentIndex, String(index + 1).padStart(2, '0'));
    transitionHeroMeta(currentDescription, items[index].dataset.description || items[index].dataset.title || '');
    const nextItem = items[(index + 1) % n];
    // poster first so the slot is never empty, then swap in a real frame of the
    // clip once one can be grabbed — guarded, since the grab is asynchronous
    // and the reel may have moved on by the time it lands
    setPreview(stillOf(nextItem));
    const nextFilm = nextItem.querySelector('video');
    if (nextFilm) {
      // never grab from the film that is on screen — seeking it would jump
      // playback (only possible with a single-slide reel)
      if (nextItem !== items[index]) filmFrame(nextFilm, (url) => {
        if (items[(index + 1) % n] === nextItem) setPreview(url);
      });
      // only the opening film is fetched up front; every other one starts
      // buffering once it is the next slide, so it is ready to cut to
      if (live && nextFilm.preload !== 'auto') { nextFilm.preload = 'auto'; nextFilm.load(); }
    }
    if (nextPreviewTitle) nextPreviewTitle.textContent = nextItem.dataset.title || '';
    dots.forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
    hasSyncedHeroMeta = true;
  }

  // the thumbnail wipes in from the right whenever its source actually changes
  function setPreview(source) {
    if (!nextPreviewImage || !source || source === previewSource) return;
    const isUpdate = Boolean(previewSource);
    previewSource = source;
    nextPreviewImage.src = source;
    if (isUpdate && !reduced) {
      nextPreviewImage.animate(
        [{ clipPath: 'inset(0 0 0 100%)' }, { clipPath: 'inset(0 0 0 0)' }],
        { duration: 620, easing: 'cubic-bezier(.65,0,.2,1)', fill: 'forwards' }
      );
    }
  }

  function transitionHeroMeta(element, value) {
    if (!element || element.textContent === value) return;
    if (!hasSyncedHeroMeta || reduced) {
      element.textContent = value;
      return;
    }
    const exit = element.animate(
      [{ clipPath: 'inset(0 0 0 0)', opacity: 1 }, { clipPath: 'inset(0 0 100% 0)', opacity: 0 }],
      { duration: 230, easing: 'cubic-bezier(.65,0,.3,1)', fill: 'forwards' }
    );
    exit.onfinish = () => {
      element.textContent = value;
      element.animate(
        [{ clipPath: 'inset(100% 0 0 0)', opacity: 0 }, { clipPath: 'inset(0 0 0 0)', opacity: 1 }],
        { duration: 420, easing: 'cubic-bezier(.25,.8,.25,1)', fill: 'forwards' }
      );
    };
  }

  function swapWord() {
    if (!em || reduced || !wordSpan) return;
    wordIndex = (wordIndex + 1) % WORDS.length;
    const outgoing = wordSpan;
    const incoming = makeWord(WORDS[wordIndex]);
    em.appendChild(incoming);
    outgoing.animate(
      [{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(-105%)', opacity: 0 }],
      { duration: 320, easing: 'ease-in', fill: 'forwards' }
    );
    incoming.animate(
      [{ transform: 'translateY(105%)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }],
      { duration: 460, easing: 'cubic-bezier(.45,0,.2,1)', fill: 'forwards' }
    ).onfinish = () => outgoing.remove();
    wordSpan = incoming;
  }

  function advance(to) {
    if (animating) return;
    to = ((to % n) + n) % n;
    if (to === index) return;
    animating = true;
    clearTimeout(timer);
    if (barAnim) barAnim.cancel();

    const current = items[index];
    const next = items[to];
    next.style.zIndex = '2';
    current.style.zIndex = '1';
    next.classList.add('is-active');
    playMedia(next);

    if (reduced) {
      current.classList.remove('is-active');
      finish(to);
      return;
    }

    next.animate(
      [{ opacity: 0, transform: 'scale(1.035)' }, { opacity: 1, transform: 'scale(1)' }],
      { duration: BLEND, easing: 'cubic-bezier(.45,0,.2,1)', fill: 'forwards' }
    );
    current.animate(
      [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.018)' }],
      { duration: BLEND, easing: 'cubic-bezier(.45,0,.2,1)', fill: 'forwards' }
    );
    swapWord();
    timer = window.setTimeout(() => {
      current.classList.remove('is-active');
      finish(to);
    }, BLEND);
  }

  function finish(to) {
    items.forEach((item, itemIndex) => {
      item.style.opacity = '';
      item.style.transform = '';
      item.style.zIndex = '';
      item.classList.toggle('is-active', itemIndex === to);
      if (itemIndex !== to) stopMedia(item);
    });
    index = to;
    syncHud();
    animating = false;
    if (!paused) cycle();
  }

  function cycle() {
    if (paused) return;
    const film = items[index].querySelector('video');
    if (film) { cycleFilm(film); return; }
    startDwell(DWELL);
  }

  // A film sets its own dwell instead of taking DWELL. Playback starts with the
  // incoming crossfade, so handing over BLEND before the last frame lands the
  // next slide exactly as the film runs out — the picture is still moving right
  // up to the cut, rather than freezing on a final frame while it fades.
  // Duration can be unknown on the first pass; wait for metadata, then measure.
  function cycleFilm(film) {
    const run = () => {
      if (paused || animating) return;
      const dur = Number.isFinite(film.duration) && film.duration > 0
        ? film.duration
        : (DWELL + BLEND) / 1000;                       // unreadable — fall back
      startDwell(Math.max(400, (dur - film.currentTime) * 1000 - BLEND));
    };
    if (film.readyState >= 1) run();
    else film.addEventListener('loadedmetadata', run, { once: true });
  }

  // one dwell clock for both cases — the HUD bar when there is one (it also
  // pauses cleanly with the toggle), a plain timer otherwise
  function startDwell(ms) {
    if (bar) {
      barAnim = bar.animate(
        [{ width: '0%' }, { width: '100%' }],
        { duration: ms, easing: 'linear', fill: 'forwards' }
      );
      barAnim.onfinish = () => advance(index + 1);
    } else {
      timer = window.setTimeout(() => advance(index + 1), ms);
    }
  }

  function setPaused(value) {
    paused = value;
    toggle?.classList.toggle('is-paused', value);
    toggle?.setAttribute('aria-pressed', String(value));
    toggle?.setAttribute('aria-label', value ? 'Play slideshow' : 'Pause slideshow');
    const media = mediaOf(items[index]);
    if (media) media.style.animationPlayState = value ? 'paused' : '';
    if (value) stopMedia(items[index]); else playMedia(items[index], false);
    if (barAnim) value ? barAnim.pause() : barAnim.play();
    if (!value && !barAnim && !animating) cycle();
  }

  toggle?.addEventListener('click', (event) => { event.stopPropagation(); setPaused(!paused); });
  prev?.addEventListener('click', (event) => { event.stopPropagation(); advance(index - 1); });
  next?.addEventListener('click', (event) => { event.stopPropagation(); advance(index + 1); });
  nextPreview?.addEventListener('click', (event) => { event.stopPropagation(); advance(index + 1); });
  root.addEventListener('click', (event) => {
    if (event.target.closest('a, button')) return;
    advance(index + 1);
  });

  if (fine && !reduced && nextPreview && nextPreviewImage) {
    let previewTargetX = 0, previewTargetY = 0, previewX = 0, previewY = 0, previewRaf = null;
    nextPreview.addEventListener('pointermove', (event) => {
      const rect = nextPreview.getBoundingClientRect();
      previewTargetX = ((event.clientX - rect.left) / rect.width - .5) * -10;
      previewTargetY = ((event.clientY - rect.top) / rect.height - .5) * -8;
      if (!previewRaf) previewRaf = requestAnimationFrame(movePreview);
    });
    nextPreview.addEventListener('pointerleave', () => {
      previewTargetX = 0;
      previewTargetY = 0;
      if (!previewRaf) previewRaf = requestAnimationFrame(movePreview);
    });
    function movePreview() {
      previewX += (previewTargetX - previewX) * .12;
      previewY += (previewTargetY - previewY) * .12;
      nextPreviewImage.style.transform = `translate(${previewX.toFixed(2)}px, ${previewY.toFixed(2)}px) scale(1.08)`;
      previewRaf = Math.abs(previewTargetX - previewX) > .05 || Math.abs(previewTargetY - previewY) > .05
        ? requestAnimationFrame(movePreview) : null;
      if (!previewRaf && previewTargetX === 0 && previewTargetY === 0) nextPreviewImage.style.transform = '';
    }
  }

  if (fine && !reduced && shift) {
    let targetX = 0, targetY = 0, currentX = 0, currentY = 0, raf = null;
    root.addEventListener('pointermove', (event) => {
      const rect = root.getBoundingClientRect();
      targetX = ((event.clientX - rect.left) / rect.width - .5) * -3.6;
      targetY = ((event.clientY - rect.top) / rect.height - .5) * -3.2;
      if (!raf) raf = requestAnimationFrame(drift);
    });
    function drift() {
      currentX += (targetX - currentX) * .065;
      currentY += (targetY - currentY) * .065;
      shift.style.transform = `translate(${currentX.toFixed(3)}%, ${currentY.toFixed(3)}%)`;
      raf = Math.abs(targetX - currentX) > .002 || Math.abs(targetY - currentY) > .002
        ? requestAnimationFrame(drift) : null;
    }
  }

  return {
    start() { if (!paused) cycle(); },
    pause() { setPaused(true); },
    // The loader can sit over the hero for a second or two, and the opening
    // slide's clock — its film, its drift, its dwell — starts at boot. Once the
    // page is actually visible, hand slide one a clean full-length run.
    restart() {
      live = true;
      syncHud();                          // now safe to buffer the next film
      if (paused) return;
      clearTimeout(timer);
      if (barAnim) { barAnim.cancel(); barAnim = null; }
      const item = items[index];
      item.classList.remove('is-active');
      void item.offsetWidth;              // reflow, so the drift keyframes rerun
      item.classList.add('is-active');
      playMedia(item);
      cycle();
    },
  };
}
